import React from 'react';
import { hasAccess } from '@components/Authorize';
import { NotificationContextProvider, useNotificationContext } from './NotificationsContext';
import { FORM_MODE, REST_API, Permissions, GeoAreaTypes } from '@awp/constants';
import { DeleteJson, PutJson } from '@awp/helpers/fetchReact';
import { toast } from 'react-toastify';

export const SUBSCRIPTION_TYPE = {
  WEATHER_STATION: 0,
  GEOGRAPHIC_AREA: 1,
};

// Constant array options for alert types
export const alertTypeDropdown = {
  options: [
    { value: 'N', label: 'Public alerts' },
    { value: 'Y', label: 'Private alerts' },
  ],
  status: null,
};

// Constant array options for active types
export const activeTypeDropdown = {
  options: [
    { value: 'Y', label: 'Active' },
    { value: 'N', label: 'Non-active' },
  ],
  status: null,
};

// Constant array for no-data frequencies
export const noDataFrequencies = [
  { value: 1, label: '1 Hour' },
  { value: 3, label: '3 Hours' },
  { value: 6, label: '6 Hours' },
  { value: 12, label: '12 Hours' },
  { value: 24, label: '1 Day' },
];

// Constant array for Area types
export const allAreaTypesOptions = [
  { value: GeoAreaTypes.CONTRACT, label: GeoAreaTypes.CONTRACT_DISPLAY },
  { value: GeoAreaTypes.EET, label: GeoAreaTypes.EET_DISPLAY },
];

// Frequencies with an additional "No Limit" option
export const frequencies = [{ value: 0, label: 'No Limit' }, ...noDataFrequencies];

// Configuration for different form modes
export const modeConfig = {
  [FORM_MODE.EDIT]: {
    readOnly: false,
    title: 'Edit Alert',
    subscribeMode: false,
  },
  [FORM_MODE.CREATE]: {
    readOnly: false,
    title: 'Create Alert',
    subscribeMode: false,
  },
  [FORM_MODE.CLONE]: {
    readOnly: false,
    title: 'Create Alert',
    subscribeMode: false,
  },
  [FORM_MODE.SUBSCRIBE]: {
    readOnly: false,
    title: 'Subscribe to Alert',
    subscribeMode: true,
  },
  default: {
    readOnly: true,
    title: 'View Alert',
    subscribeMode: false,
  },
};

/**
 * Returns the default DTO for form initialization.
 *
 * @param {object} notificationMethods - The notification methods (email, sms).
 * @returns {object} The default DTO object.
 */
export const defaultDto = (notificationMethods) => {
  const { email, sms } = notificationMethods;

  return {
    id: null,
    name: '',
    enableDateRange: true,
    startDate: null,
    endDate: null,
    email,
    sms,
    frequency: { value: 1, label: '1 Hour' },
    isActive: true,
    isPrivate: 'Y',
    areaType: 'weatherStation',
  };
};

/**
 * Formats the option label with an icon.
 *
 * @param {object} option - The option object with value and label properties.
 * @returns {JSX.Element} The formatted option label with an icon.
 */
export const formatOptionLabel = (option) => {
  const _icon = option.value === 'N' ? 'fa-globe' : option.value === 'Y' ? 'fa-user' : '';
  return (
    <div style={{ display: 'flex' }}>
      <div>
        <i className={`fas fa-solid ${_icon} mx-1 my-1`} style={{ color: 'blue' }} />
        {` ${option.label}`}
      </div>
    </div>
  );
};

/**
 * Renders the action menu for each notification row.
 *
 * @param {object} row - The row data for the notification.
 * @param {object} context - The context object containing form mode, state setters, and other relevant data.
 * @returns {JSX.Element} The action menu component.
 */
export const actionMenu = (row, context) => {
  const { setMode, setNotificationId, data, setData, setShowYesNoModal } = context;

  const record = row.row.original;
  const isNoDataEvt = record.IsForMissingObservations === 'Y';

  return (
    <div>
      {((hasAccess([Permissions.WritePersonalAlert]) && record.IsPrivate === 'Y' && !isNoDataEvt) ||
        (hasAccess([Permissions.WritePublicAlert]) && !isNoDataEvt)) && (
        <i
          className="fas fa-edit mx-1 my-1 awp-img-btn"
          title="Edit"
          onClick={() => {
            setMode(FORM_MODE.EDIT);
            setNotificationId(record.NotificationTriggerId);
          }}
        ></i>
      )}
      <i
        className="fas fa-eye mx-1 my-1 awp-img-btn"
        title="View"
        onClick={() => {
          setMode(FORM_MODE.VIEW);
          setNotificationId(record.NotificationTriggerId);
        }}
      ></i>
      {((hasAccess([Permissions.WritePersonalAlert]) && record.IsPrivate === 'Y' && !isNoDataEvt) ||
        (hasAccess([Permissions.WritePublicAlert]) && !isNoDataEvt)) && (
        <i
          className="fas fa-trash mx-1 my-1 awp-img-btn"
          title="Delete"
          onClick={() => {
            setNotificationId(record.NotificationTriggerId);
            setShowYesNoModal(true);
          }}
        ></i>
      )}
      {record.IsPrivate === 'N' && (
        <i
          className="fas fa-plus mx-1 my-1 awp-img-btn"
          title="Subscribe"
          onClick={() => {
            setMode(FORM_MODE.SUBSCRIBE);
            setNotificationId(record.NotificationTriggerId);
          }}
        ></i>
      )}
      {record.IsPrivate === 'N' && record.NotificationSubscriptions && record.NotificationSubscriptions.length > 0 && (
        <i
          className="fas fa-minus mx-1 my-1 awp-img-btn"
          title="Unsubscribe"
          onClick={() => {
            handleUnsubscribe(record.NotificationTriggerId, record.Name, data, setData);
          }}
        ></i>
      )}
      {!isNoDataEvt && (
        <i
          className="fas fa-copy mx-1 my-1 awp-img-btn"
          title="Clone"
          onClick={() => {
            setMode(FORM_MODE.CLONE);
            setNotificationId(record.NotificationTriggerId);
          }}
        />
      )}
    </div>
  );
};

/**
 * Handles the deletion of a notification.
 *
 * @returns {Promise<void>} A promise that resolves when the delete operation is complete.
 */
export const handleDelete = async (context) => {
  const { setShowYesNoModal, notificationId, data, setData } = context; // useNotificationContext(NotificationContextProvider);

  setShowYesNoModal(false); // Close the confirmation modal

  const url = REST_API.NOTIFICATIONS + notificationId; // Construct the API endpoint URL

  const resp = await DeleteJson(url); // Send the delete request to the API
  if (resp.ok) {
    toast.success('Successfully deleted'); // Show success message
    const index = data.findIndex((x) => x.NotificationTriggerId === notificationId); // Find the index of the deleted notification
    if (index < 0) return;
    data[index].IsDeleted = 'Y'; // Mark the notification as deleted in the local state
    setData([...data]); // Update the state with the modified data
  } else {
    toast.error('Error occurred while deleting'); // Show error message
  }
};

/**
 * Handles the unsubscription from a notification.
 *
 * @param {string} notificationId - The ID of the notification.
 * @param {string} alertName - The name of the alert.
 * @param {Array} data - The current notification data.
 */
const handleUnsubscribe = async (notificationId, alertName, data, setData) => {
  const url = REST_API.NOTIFICATIONS + notificationId + '/unsubscribe';

  const resp = await PutJson(url);
  if (resp.ok) {
    toast.success(
      `You have successfully unsubscribed from receiving "${alertName}" notification. To re-subscribe, please click on the "+" button.`,
    );
    const entity = await resp.json();
    const index = data.findIndex((x) => x.NotificationTriggerId === notificationId);
    if (index < 0) return;
    data[index] = entity;
    setData([...data]);
  } else {
    toast.error('Error occurred while unsubscribing');
  }
};

/**
 * Retrieves the default notification methods for a given notification.
 *
 * @param {object} notification - The notification object.
 * @returns {object} An object containing the default notification methods.
 */
export const getDefaultNotificationMethods = (notification) => {
  const defaults = { email: true, sms: false }; // Default values for notification methods
  if (!notification) return defaults; // Return defaults if no notification is provided

  if (notification.NotificationSubscriptions.length > 0) {
    const firstSubscription = notification.NotificationSubscriptions[0]; // Get the first subscription
    return { email: firstSubscription.IsEmailNotify === 'Y', sms: firstSubscription.IsSmsNotify === 'Y' }; // Return notification methods based on subscription
  }

  return defaults; // Return defaults if no subscriptions exist
};

/**
 * Filters the weather stations based on the selected trigger group values.
 *
 * @param {object} values - The form values containing the trigger group.
 * @returns {Array} An array of filtered weather stations.
 */
export const filterWeatherStations = (values) => {
  const { weatherStationsObsTypes, stationFilter } = useNotificationContext(NotificationContextProvider);

  if (!stationFilter) return weatherStationsObsTypes.options; // Return all weather stations if no filter is set

  if (!values.TriggerGroup) return []; // Return an empty array if no trigger group is provided

  if (values.name === 'NO DATA') {
    const filtered = weatherStationsObsTypes.options.filter(
      (station) => station.observationTypeIds.length > 0 && station.Active,
    );

    return filtered;
  }

  let observationTypeIds = []; // Array to store unique observation type IDs

  const triggerGroup = values.TriggerGroup;

  // Iterate over the trigger group keys to collect observation type IDs
  Object.keys(triggerGroup).forEach((key) => {
    if (key.startsWith('observationType') && !observationTypeIds.includes(triggerGroup[key].value)) {
      observationTypeIds.push(triggerGroup[key].value); // Add unique observation type IDs to the array
    }
  });

  // Filter weather stations based on matching observation type IDs
  const filtered = weatherStationsObsTypes.options.filter(
    (station) => station.observationTypeIds.some((typeId) => observationTypeIds.includes(typeId)) && station.Active,
  );

  return filtered; // Return the filtered weather stations
};
